import React, { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { SettingsMotionIcon } from "@/components/ui/settings-motion";
import { StatisticsEmptyState } from "@/views/Dashboard/Statistics/components/statisticsEmptyState";
import { useActiveBrokerAccountOptional } from "../../hooks/useActiveBrokerAccount";
import { useInstrumentSetupOptional } from "../../contexts/InstrumentSetupContext";

/** Shared resolve/hydrate status for instrument-setup gating. */
export function useInstrumentSetupGateStatus(
  bypass = false,
  setupSymbol?: string | null
): {
  checking: boolean;
  blocking: boolean;
  openSetup: (accountCode: string, options?: { mandatory?: boolean }) => Promise<void>;
  activeAccountCode: string | null | undefined;
} {
  const brokerCtx = useActiveBrokerAccountOptional();
  const setup = useInstrumentSetupOptional();
  // Public demo / non-Admin trees have neither provider — never block features there.
  const outsideProviders = !brokerCtx || !setup;
  const effectiveBypass = bypass || outsideProviders;

  const activeAccountCode = brokerCtx?.activeAccountCode;
  const accountsLoading = brokerCtx?.loading ?? false;
  const openSetup =
    setup?.openSetup ??
    (async (_accountCode: string, _options?: { mandatory?: boolean }) => {
      /* no-op outside InstrumentSetupProvider */
    });
  const hydratePendingForActiveAccount =
    setup?.hydratePendingForActiveAccount ?? (async () => false);

  const hydrateRef = useRef(hydratePendingForActiveAccount);
  hydrateRef.current = hydratePendingForActiveAccount;

  const [hydratedPending, setHydratedPending] = useState<boolean | null>(null);
  const [hydrating, setHydrating] = useState(false);
  const [initialHydrateComplete, setInitialHydrateComplete] = useState(false);

  useEffect(() => {
    setInitialHydrateComplete(false);
    setHydratedPending(null);
    setHydrating(false);
  }, [activeAccountCode, setupSymbol]);

  useEffect(() => {
    if (effectiveBypass || accountsLoading || !activeAccountCode || initialHydrateComplete) {
      return;
    }

    let cancelled = false;
    setHydrating(true);
    void hydrateRef.current({ symbol: setupSymbol?.trim() || undefined }).then((pending) => {
      if (!cancelled) {
        setHydratedPending(pending);
        setHydrating(false);
        setInitialHydrateComplete(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveBypass, accountsLoading, activeAccountCode, initialHydrateComplete, setupSymbol]);

  const checking =
    !effectiveBypass &&
    !initialHydrateComplete &&
    (accountsLoading || hydrating || hydratedPending === null);

  const blocking = !effectiveBypass && initialHydrateComplete && hydratedPending === true;

  return { checking, blocking, openSetup, activeAccountCode };
}

export function InstrumentSetupGate(props: {
  featureLabel: string;
  bypass?: boolean;
  /** Optional symbol scope — Layer A setup only, not OHLC lane availability. */
  setupSymbol?: string | null;
  children: React.ReactNode;
}): React.ReactElement {
  const { checking, blocking, openSetup, activeAccountCode } = useInstrumentSetupGateStatus(
    props.bypass,
    props.setupSymbol
  );

  const hideChildren = !props.bypass && (checking || blocking);

  return (
    <>
      {!props.bypass && checking ? (
        <StatisticsEmptyState
          variant="loading"
          title="Checking instrument setup…"
          description="Please wait while we confirm market data is ready."
        />
      ) : null}

      {!props.bypass && !checking && blocking ? (
        <Empty className="min-h-[320px] w-full border-0 bg-transparent py-12 shadow-none">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <SettingsMotionIcon size={16} className="text-[color:var(--brand-tropical-surf)]" />
            </EmptyMedia>
            <EmptyTitle className="font-[family-name:var(--font-primary)] text-[color:var(--brand-tropical-surf)]">
              Confirm your instruments to use {props.featureLabel}
            </EmptyTitle>
            <EmptyDescription className="max-w-md text-sm text-muted-foreground">
              Market data must be configured for your traded symbols before charts and OHLC analysis can
              run. Complete instrument setup to continue.
            </EmptyDescription>
          </EmptyHeader>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="mt-4 h-8 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={() => {
              if (activeAccountCode) {
                void openSetup(activeAccountCode, { mandatory: true });
              }
            }}
          >
            Configure instruments
          </Button>
        </Empty>
      ) : null}

      {/* Keep children mounted once shown — opening setup wizard must not collapse this tree. */}
      <div
        hidden={hideChildren}
        aria-hidden={hideChildren}
        className={hideChildren ? undefined : "contents"}
      >
        {props.children}
      </div>
    </>
  );
}
